import { useCallback, useEffect, useRef, useState } from 'react';
import { useApi } from '../context';
import type { Handover, HandoverDetail, HandoverItem, HandoverListRow } from '../types';

/** Editable item carries a stable client key (server id for saved rows, generated for new). */
interface EditItem extends HandoverItem {
  key: string;
}

/** On-call handover: mark calls, compile them with actions and free-form lines, submit, export PDF. */
export function HandoversPage() {
  const api = useApi();
  const [list, setList] = useState<HandoverListRow[]>([]);
  const [selected, setSelected] = useState<HandoverDetail | null>(null);
  const [error, setError] = useState<string | null>(null);

  const loadList = useCallback(() => api.get<HandoverListRow[]>('/api/handovers').then(setList, (e: Error) => setError(e.message)), [api]);
  useEffect(() => { void loadList(); }, [loadList]);

  const open = (id: string) => api.get<HandoverDetail>(`/api/handovers/${id}`).then(setSelected, (e: Error) => setError(e.message));
  const create = () => api.post<HandoverDetail>('/api/handovers', {}).then((d) => { setSelected(d); void loadList(); }, (e: Error) => setError(e.message));

  if (selected) {
    return <HandoverEditor detail={selected} onError={setError} onClose={() => { setSelected(null); void loadList(); }} onReload={() => open(selected.handover.id)} />;
  }

  return (
    <>
      <h1>Handover</h1>
      {error && <p className="error">{error}</p>}
      <div className="filters">
        <button className="primary" onClick={create}>Create handover</button>
        <span className="sub">Compiles the calls you've marked “include in handover”.</span>
      </div>
      <table>
        <thead><tr><th>Created</th><th>By</th><th>Items</th><th>Status</th></tr></thead>
        <tbody>
          {list.map((h) => (
            <tr key={h.id}>
              <td><button className="link" onClick={() => open(h.id)}>{new Date(h.created_at).toLocaleString()}</button></td>
              <td>{h.created_by_name ?? '—'}</td>
              <td>{h.item_count}</td>
              <td>{h.submitted_at ? <span className="badge completed">submitted</span> : <span className="badge">draft</span>}</td>
            </tr>
          ))}
          {list.length === 0 && <tr><td colSpan={4}>No handovers yet</td></tr>}
        </tbody>
      </table>
    </>
  );
}

function HandoverEditor({ detail, onClose, onError, onReload }:
  { detail: HandoverDetail; onClose: () => void; onError: (m: string) => void; onReload: () => void }) {
  const api = useApi();
  const keyer = useRef(0);
  const submitted = !!detail.handover.submitted_at;
  const [items, setItems] = useState<EditItem[]>(() => detail.items.map((it) => ({ ...it, key: it.id ?? `k${keyer.current++}` })));
  const [arrivedHome, setArrivedHome] = useState<Handover['arrived_home']>(detail.handover.arrived_home);
  const [arrivedNotes, setArrivedNotes] = useState(detail.handover.arrived_home_notes ?? '');
  const [busy, setBusy] = useState(false);

  const update = (i: number, patch: Partial<EditItem>) => setItems((s) => s.map((it, j) => (j === i ? { ...it, ...patch } : it)));
  const move = (i: number, d: -1 | 1) => setItems((s) => { const n = [...s]; const j = i + d; if (j < 0 || j >= n.length) return s; [n[i], n[j]] = [n[j]!, n[i]!]; return n; });
  const remove = (i: number) => setItems((s) => s.filter((_, j) => j !== i));
  const addNote = (at: number) => setItems((s) => { const n = [...s]; n.splice(at, 0, { key: `k${keyer.current++}`, kind: 'note', item_text: '', action: '' }); return n; });

  async function save() {
    setBusy(true);
    try {
      const body = {
        items: items.map((it) => ({ id: it.id, kind: it.kind, action: it.action ?? null, itemText: it.item_text ?? null })),
        arrivedHome, arrivedHomeNotes: arrivedNotes || null,
      };
      const d = await api.put<HandoverDetail>(`/api/handovers/${detail.handover.id}`, body);
      setItems(d.items.map((it) => ({ ...it, key: it.id ?? `k${keyer.current++}` })));
    } catch (e) { onError((e as Error).message); } finally { setBusy(false); }
  }
  async function submit() {
    if (!confirm('Submit this handover? It will be locked and the calls it covers will be cleared from the next handover.')) return;
    setBusy(true);
    try { await save(); await api.post(`/api/handovers/${detail.handover.id}/submit`, {}); onReload(); }
    catch (e) { onError((e as Error).message); } finally { setBusy(false); }
  }
  async function exportPdf() {
    try {
      const blob = await api.blob(`/api/handovers/${detail.handover.id}/pdf`);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = 'on-call-handover.pdf'; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { onError((e as Error).message); }
  }

  return (
    <>
      <div className="filters">
        <button className="link" onClick={onClose}>← Handovers</button>
        <h1 style={{ margin: 0 }}>Handover</h1>
        {submitted ? <span className="badge completed">submitted</span> : <span className="badge">draft</span>}
        <span className="sub">{detail.handover.created_by_name} · {new Date(detail.handover.created_at).toLocaleString()}</span>
      </div>

      <ol className="handover-items">
        {items.map((it, i) => (
          <li key={it.key} className="line-card">
            <div className="filters">
              {!submitted && (
                <span className="reorder">
                  <button className="link" disabled={i === 0} onClick={() => move(i, -1)}>↑</button>
                  <button className="link" disabled={i === items.length - 1} onClick={() => move(i, 1)}>↓</button>
                </span>
              )}
              {it.kind === 'call' ? (
                <div>
                  <strong>{it.caller ?? 'Unknown'}</strong>{' '}
                  <span className="sub">{it.call_at ? new Date(it.call_at).toLocaleString() : ''}{it.call_type ? ` · ${it.call_type}` : ''}</span>
                  {it.call_notes && <div className="sub">Notes: {it.call_notes}</div>}
                </div>
              ) : (
                <input style={{ flex: 1 }} placeholder="Line item…" value={it.item_text ?? ''} disabled={submitted}
                  onChange={(e) => update(i, { item_text: e.target.value })} />
              )}
              <span className="spacer" />
              {!submitted && <button className="link danger" onClick={() => remove(i)}>remove</button>}
            </div>
            <textarea placeholder="Action…" value={it.action ?? ''} disabled={submitted}
              onChange={(e) => update(i, { action: e.target.value })} style={{ minHeight: '2.5rem' }} />
            {!submitted && <button className="link" onClick={() => addNote(i + 1)}>+ add line below</button>}
          </li>
        ))}
      </ol>
      {!submitted && <button onClick={() => addNote(items.length)}>+ Add line item</button>}

      <div className="line-card" style={{ marginTop: '1rem' }}>
        <strong>Did all staff members arrive home safely?</strong>
        <div className="filters">
          {(['yes', 'no', 'na'] as const).map((v) => (
            <label key={v} className="sub"><input type="radio" name="arrived" checked={arrivedHome === v} disabled={submitted}
              onChange={() => setArrivedHome(v)} /> {v === 'yes' ? 'Yes' : v === 'no' ? 'No' : 'Not applicable'}</label>
          ))}
        </div>
        <textarea placeholder="Notes (any additional information)…" value={arrivedNotes} disabled={submitted}
          onChange={(e) => setArrivedNotes(e.target.value)} />
      </div>

      <div className="filters" style={{ marginTop: '1rem' }}>
        {!submitted && <button className="primary" onClick={save} disabled={busy}>Save</button>}
        {!submitted && <button onClick={submit} disabled={busy}>Submit</button>}
        <button onClick={exportPdf}>Export PDF</button>
      </div>
    </>
  );
}
